import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { WebChatMessage } from '../api/web-chat';

export interface ChatStatusEvent {
  ticketId: number;
  status: string;
  awaitingRating: boolean;
  customerRating: string | null;
}

type Handlers = {
  onMessage?: (message: WebChatMessage) => void;
  onStatus?: (event: ChatStatusEvent) => void;
  onConnect?: () => void;
};

/**
 * Socket en tiempo real del web-chat (cliente). Se conecta al namespace
 * /web-chat autenticándose por sessionToken y escucha message:new + chat:status.
 * Sustituye al polling cada 3s. Solo conecta mientras `enabled` es true
 * (conversación activa o esperando valoración).
 */
export function useWebChatSocket(
  sessionToken: string | null,
  enabled: boolean,
  handlers: Handlers
) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!sessionToken || !enabled) return;

    const socket: Socket = io('/web-chat', {
      path: '/socket.io/',
      auth: { sessionToken },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => handlersRef.current.onConnect?.());

    socket.on('message:new', (data: { ticketId: number; message: WebChatMessage }) => {
      if (data?.message) handlersRef.current.onMessage?.(data.message);
    });

    socket.on('chat:status', (data: ChatStatusEvent) => {
      handlersRef.current.onStatus?.(data);
    });

    return () => {
      socket.disconnect();
    };
  }, [sessionToken, enabled]);
}
